import { Injectable, Optional } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { BaseService, GTMAnalyticsService } from 'src/app/shared/services';
import { DnDesignerService } from './dn-designer.service';
import { AiJobState, AiPreviewJobDto } from '../models';

const STORAGE_KEY = 'dn-ai-preview-job';
const POLL_INTERVAL_MS = 3000;
// About 25s of consecutive failures — long enough to ride out a blip, short enough not to leave a stuck spinner.
const MAX_CONSECUTIVE_POLL_ERRORS = 8;

interface SavedJob {
    jobId: string;
    brief: string;
    format: string;
    startedAtMs: number;
}

/**
 * Owns the one AI design generation a user can have running. A generation takes minutes, so it runs as a
 * background job on the server: this starts it, polls its status, and exposes it as state that a floating
 * pill (visible on every page) renders — the user is never stuck behind a modal. The job id is kept in
 * sessionStorage so a page reload picks the job back up instead of losing it.
 */
@Injectable({ providedIn: 'root' })
export class AiPreviewJobService {
    readonly state$ = new BehaviorSubject<AiJobState>({ kind: 'idle' });

    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private consecutivePollErrors = 0;
    private meta: { brief: string; format: string } = { brief: '', format: 'landscape' };

    constructor(private designer: DnDesignerService, private baseService: BaseService, @Optional() private gtm?: GTMAnalyticsService) {
        this.resume();
    }

    /** DN-4379: every failed start says why, and is counted, instead of a generic "unexpected error". */
    private startFailed(reason: 'no_credits' | 'invalid_brief' | 'connection_lost' | 'generation_failed', message: string, status?: number): false {
        this.baseService.error(message);
        this.gtm?.trackCustomEvent('design_generate_failed', { reason, status: status ?? null });
        return false;
    }

    get isActive(): boolean {
        return this.state$.value.kind === 'generating';
    }

    /** Resolves true when a job is now running (new, or the one already in flight), false if nothing started. */
    async start(brief: string, format: string, attachmentUrl?: string): Promise<boolean> {
        if (this.isActive) return false;
        this.meta = { brief, format };
        try {
            this.track(await firstValueFrom(this.designer.startPreviewJob(brief, format, attachmentUrl)));
            return true;
        } catch (error) {
            if (error instanceof HttpErrorResponse && error.status === 409 && error.error?.jobId) {
                // Already generating one (another tab, or before a reload): follow that run instead.
                const saved = this.readSaved();
                this.meta = saved && saved.jobId === error.error.jobId ? { brief: saved.brief, format: saved.format } : { brief: '', format };
                this.baseService.info('You already have a design being created — showing its progress.');
                this.track(error.error);
                return true;
            }
            if (error instanceof HttpErrorResponse && error.status === 429) {
                return this.startFailed('no_credits', error.error?.errorMessage || "You've used all your AI designs for this month.", error.status);
            }
            if (error instanceof HttpErrorResponse && error.status === 400) {
                return this.startFailed('invalid_brief', error.error?.errorMessage || 'Please describe the design you want, then try again.', error.status);
            }
            if (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 504)) {
                return this.startFailed('connection_lost', 'Connection lost before your design could start. Check your connection and try again.', error.status);
            }
            return this.startFailed('generation_failed', 'We could not start your design. Please try again.', error instanceof HttpErrorResponse ? error.status : undefined);
        }
    }

    /**
     * Fetches a job by id — how a notification reopens a design from another tab or a later session.
     * Returns it only when there is something to show (ready, or still running); otherwise tells the user why not.
     */
    async lookup(jobId: string): Promise<AiPreviewJobDto | null> {
        try {
            const job = await firstValueFrom(this.designer.getPreviewJob(jobId));
            if (job.status === 'Error') {
                this.baseService.error(job.errorMessage || 'Design generation failed. Please try again.');
                return null;
            }
            return job;
        } catch (error) {
            if (error instanceof HttpErrorResponse && error.status === 404) {
                this.baseService.info('That design is no longer available — finished designs are only kept for a short while. Please generate it again.');
            } else {
                this.baseService.processErrorResponse(error);
            }
            return null;
        }
    }

    /** Follows a job that was started elsewhere (e.g. another tab) and is still running. */
    adopt(job: AiPreviewJobDto): void {
        if (this.isActive) return;
        this.meta = { brief: job.brief ?? '', format: job.format ?? 'landscape' };
        this.track(job);
    }

    /** Clears a finished (ready or failed) job — after the design was used, or the user dismissed it. */
    dismiss(): void {
        this.stopPolling();
        this.clearSaved();
        this.state$.next({ kind: 'idle' });
    }

    private track(job: AiPreviewJobDto): void {
        this.consecutivePollErrors = 0;
        this.save({ jobId: job.jobId, brief: this.meta.brief, format: this.meta.format, startedAtMs: Date.now() - job.elapsedSeconds * 1000 });
        this.apply(job);
    }

    private apply(job: AiPreviewJobDto, announce = true): void {
        if (job.status === 'Generating') {
            this.state$.next({
                kind: 'generating',
                jobId: job.jobId,
                brief: this.meta.brief,
                format: this.meta.format,
                startedAtMs: Date.now() - job.elapsedSeconds * 1000,
                step: job.step ?? undefined,
                maxSteps: job.maxSteps ?? undefined,
                phase: job.phase ?? undefined,
            });
            this.schedulePoll();
        } else if (job.status === 'Ready' && job.result) {
            this.stopPolling();
            this.state$.next({ kind: 'ready', jobId: job.jobId, brief: this.meta.brief, format: this.meta.format, result: job.result });
            // A job restored after a page reload is already-known news, not a fresh completion to announce.
            if (announce) this.baseService.success('Your design is ready — review it to continue.');
        } else {
            this.fail(job.errorMessage || 'Design generation failed. Please try again.');
        }
    }

    private fail(message: string): void {
        this.stopPolling();
        this.clearSaved();
        this.state$.next({ kind: 'failed', message, brief: this.meta.brief, format: this.meta.format });
    }

    private schedulePoll(): void {
        this.stopPolling();
        this.pollTimer = setTimeout(() => this.pollOnce(), POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private async pollOnce(announce = true): Promise<void> {
        const current = this.state$.value;
        if (current.kind !== 'generating') return;
        try {
            const job = await firstValueFrom(this.designer.getPreviewJob(current.jobId));
            this.consecutivePollErrors = 0;
            this.apply(job, announce);
        } catch (error) {
            if (error instanceof HttpErrorResponse && error.status === 404) {
                // The job is kept in the database for every server instance to read, so a 404 now really means this id is
                // unknown or has expired (finished jobs are only kept for a while) — nothing left to wait for.
                this.fail("We couldn't find this design — it may have expired. Please generate it again.");
                return;
            }
            this.consecutivePollErrors += 1;
            if (this.consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
                this.fail("We couldn't reach the server to check on your design. Check your connection and try again.");
                return;
            }
            this.schedulePoll();
        }
    }

    private resume(): void {
        const saved = this.readSaved();
        if (!saved) return;
        this.meta = { brief: saved.brief, format: saved.format };
        this.state$.next({ kind: 'generating', jobId: saved.jobId, brief: saved.brief, format: saved.format, startedAtMs: saved.startedAtMs });
        void this.pollOnce(false);
    }

    // sessionStorage can throw (blocked storage, private mode); the job still works without persistence.
    private readSaved(): SavedJob | null {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            return raw ? (JSON.parse(raw) as SavedJob) : null;
        } catch {
            return null;
        }
    }

    private save(job: SavedJob): void {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(job));
        } catch { /* see readSaved */ }
    }

    private clearSaved(): void {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch { /* see readSaved */ }
    }
}
